import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Button, Alert, Input } from '../../ui/components';
import { Briefcase, BarChart3, Users, Wallet, Eye, EyeOff, Info } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const ReferralPartnerLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Demo credential fallback
      const demoAccounts = [
        { email: 'partner@demo.com', password: 'demo123', id: 'demo-001', firstName: 'Jordan', lastName: 'Rivera', companyName: 'Rivera Realty Group', referralCode: 'RIVERA2025', partnerType: 'real_estate_agent' },
        { email: 'realtor@realestate.com', password: 'demo123', id: 'demo-002', firstName: 'Alex', lastName: 'Chen', companyName: 'Chen & Associates', referralCode: 'CHEN2025', partnerType: 'real_estate_agent' },
      ];

      const demoMatch = demoAccounts.find(
        (a) => a.email === email.toLowerCase() && a.password === password
      );

      if (demoMatch) {
        localStorage.setItem('lk_partner_session', JSON.stringify({
          id: demoMatch.id,
          email: demoMatch.email,
          firstName: demoMatch.firstName,
          lastName: demoMatch.lastName,
          companyName: demoMatch.companyName,
          referralCode: demoMatch.referralCode,
          partnerType: demoMatch.partnerType,
        }));
        navigate('/referral/dashboard');
        return;
      }

      // Check if partner exists and is active in Supabase
      const { data: partner, error: partnerError } = await supabase
        .from('referral_partners')
        .select('*')
        .eq('email', email.toLowerCase())
        .single();

      if (partnerError || !partner) {
        setError('No partner account found with this email. Try: partner@demo.com / demo123');
        setLoading(false);
        return;
      }

      if (partner.status !== 'active') {
        setError('Your partner account is not active. Please contact LoanKnow support.');
        setLoading(false);
        return;
      }

      // For demo purposes, accept any password or use a simple check
      // In production, this would use proper password hashing
      if (password.length < 4) {
        setError('Invalid password.');
        setLoading(false);
        return;
      }

      // Store partner session
      localStorage.setItem('lk_partner_session', JSON.stringify({
        id: partner.id,
        email: partner.email,
        firstName: partner.first_name,
        lastName: partner.last_name,
        companyName: partner.company_name,
        referralCode: partner.referral_code,
        partnerType: partner.partner_type,
      }));

      navigate('/referral/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const FeatureRow = ({ icon: Icon, title, subtitle }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)', padding: 'var(--lk-space-3)', borderRadius: 'var(--lk-radius-md)', background: 'rgba(255,255,255,0.6)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 'var(--lk-radius-md)', background: 'rgba(0,102,204,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={24} color="#0066cc" />
      </div>
      <div>
        <div style={{ fontWeight: 600, color: '#333' }}>{title}</div>
        <div style={{ fontSize: 'var(--lk-text-sm)', color: '#666' }}>{subtitle}</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
      {/* Left Side - Illustration */}
      <div style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 'var(--lk-space-8)' }}>
        <div style={{ maxWidth: 500, textAlign: 'center' }}>
          <Briefcase size={64} color="#0066cc" style={{ marginBottom: 'var(--lk-space-4)' }} />
          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#0066cc', marginBottom: 'var(--lk-space-3)' }}>Partner Dashboard</h1>
          <p style={{ fontSize: 'var(--lk-text-lg)', color: '#555', marginBottom: 'var(--lk-space-6)' }}>
            Access your referral dashboard, track performance metrics, and manage your partnership with LoanKnow.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
            <FeatureRow icon={BarChart3} title="Performance Metrics" subtitle="Track your referral success" />
            <FeatureRow icon={Users} title="Client Management" subtitle="View all your referrals" />
            <FeatureRow icon={Wallet} title="Commission Tracking" subtitle="Monitor your earnings" />
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 'var(--lk-space-8)', background: 'white' }}>
        <div style={{ width: '100%', maxWidth: 440 }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--lk-space-5)' }}>
            <img src="/assets/loanknow-logo.svg" alt="LoanKnow" style={{ height: 48, width: 'auto', marginBottom: 'var(--lk-space-3)' }} />
            <h2 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-2)' }}>Partner Portal</h2>
            <p style={{ color: 'var(--lk-text-tertiary)' }}>Sign in to view your referrals and track performance</p>
          </div>

          {error && (
            <Alert variant="error" style={{ marginBottom: 'var(--lk-space-3)' }}>{error}</Alert>
          )}

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 'var(--lk-space-3)' }}>
              <label style={{ display: 'block', fontWeight: 500, fontSize: 'var(--lk-text-sm)', marginBottom: 4 }}>Email Address</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="partner@example.com"
                required
              />
            </div>

            <div style={{ marginBottom: 'var(--lk-space-3)' }}>
              <label style={{ display: 'block', fontWeight: 500, fontSize: 'var(--lk-text-sm)', marginBottom: 4 }}>Password</label>
              <div style={{ display: 'flex', gap: 4 }}>
                <div style={{ flex: 1 }}>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                </div>
                <Button variant="secondary" onClick={() => setShowPassword(!showPassword)} style={{ flexShrink: 0 }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </div>

            <Button type="submit" variant="primary" loading={loading} style={{ width: '100%', marginBottom: 'var(--lk-space-3)' }}>
              Sign In
            </Button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 'var(--lk-space-4)' }}>
            <p style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>
              Not a partner yet?{' '}
              <a href="mailto:partners@loanknow.com" style={{ fontWeight: 600, textDecoration: 'none', color: 'var(--lk-primary-500)' }}>Contact us to join</a>
            </p>
          </div>

          <Surface padding="var(--lk-space-3)" style={{ marginTop: 'var(--lk-space-3)', background: 'var(--lk-gray-50)' }}>
            <p style={{ color: 'var(--lk-text-tertiary)', textAlign: 'center', fontSize: 'var(--lk-text-sm)', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Info size={14} /> For demo: Use any registered partner email with any password (4+ chars)
            </p>
          </Surface>
        </div>
      </div>
    </div>
  );
};

export default ReferralPartnerLoginPage;
